import { Card } from "@/ingot";
import { type JSX } from "react";
import { useTranslation } from "react-i18next";

import { useApplyManufacturabilityRepair } from "@/api/queries/quotes";

interface Props {
  quoteSlug: string;
  /**
   * `true` when at least one non-blocking manufacturability warning is
   * active for the current configurator pick (`tiny_hole` etc.). The
   * CTA hides itself otherwise — the customer either has nothing to
   * fix (no warnings) or a blocking issue that the auto-fix can't
   * resolve (they must change material/variant instead).
   */
  hasFixableWarnings: boolean;
  /**
   * `true` once a `RepairedGeometry` row carrying manufacturability
   * fixes already exists — the consent CTA hides itself so the customer
   * doesn't double-apply.
   */
  alreadyApplied: boolean;
  /** KAN-2068 — the live picks the repair resolves its bounds for. */
  dimensions?: Record<string, unknown>;
}

/**
 * Explicit-consent CTA for the per-process manufacturability auto-fix.
 *
 * PR-C of the auto-repair refactor flipped the customer flow so the
 * upload pipeline only emits warnings; this component is the button
 * the configurator surfaces when the live manufacturability check
 * shows fixable issues. Clicking it calls
 * `POST /quotes/{slug}/auto-repair-manufacturability`, which runs the
 * per-process auto-fix pass and upserts the `RepairedGeometry` row.
 * The existing `RepairCompare` component (which subscribes to
 * `GET /repair`) automatically re-renders with the new payload once
 * the mutation invalidates the cache.
 */
export function ManufacturabilityRepairCta({
  quoteSlug,
  hasFixableWarnings,
  alreadyApplied,
  dimensions,
}: Props): JSX.Element | null {
  const { t } = useTranslation("quote");
  const apply = useApplyManufacturabilityRepair(quoteSlug);

  if (!hasFixableWarnings || alreadyApplied) return null;

  const errorCode = apply.error
    ? (apply.error as { code?: string }).code ?? "unknown"
    : null;

  return (
    <Card padded data-testid="manufacturability-repair-cta">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-medium text-ink">
            {t("repair.manufacturabilityCta.title", {
              defaultValue: "Opravit varování vyrobitelnosti?",
            })}
          </p>
          <p className="text-[12px] text-ink-3">
            {t("repair.manufacturabilityCta.body", {
              defaultValue:
                "Můžeme automaticky zvětšit příliš malé díry / zaoblit ostré rohy podle limitů vašeho dodavatele. Původní DXF zůstává neměnný.",
            })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => apply.mutate(dimensions)}
          disabled={apply.isPending}
          data-testid="manufacturability-repair-apply"
          className="inline-flex h-9 items-center justify-center rounded-md bg-accent px-3 text-sm font-medium text-white shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
        >
          {apply.isPending
            ? t("repair.manufacturabilityCta.applying", {
                defaultValue: "Opravuji…",
              })
            : t("repair.manufacturabilityCta.apply", {
                defaultValue: "Opravit automaticky",
              })}
        </button>
      </div>
      {errorCode && (
        <p className="mt-2 text-[12px] text-danger" role="alert">
          {t(`repair.manufacturabilityCta.errors.${errorCode}`, {
            defaultValue: t("repair.manufacturabilityCta.errors.default", {
              defaultValue: "Opravu se nepodařilo aplikovat.",
            }),
          })}
        </p>
      )}
    </Card>
  );
}
